import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { change, Field, reduxForm, getFormValues } from 'redux-form';
import { Card, Grid, Dropdown, Icon, Button } from 'semantic-ui-react';
import moment from 'moment';
import classnames from 'classnames';

import { DC_IMPORT_SETTINGS_FORM } from 'Common/FormEnum';
import { Filter, Main } from 'Containers/';
import { postImportFile } from './state/common/actions';
import {
  saveSelectedConfig,
  getConfigList,
  analyzeFile,
  setAnalysisResult,
  setFileConfirmedOk,
  queueImport,
  clearAnalyzeResults,
  clearImporStatusError,
  getUnmatchedCount,
} from './state/hub/actions';
import { getControlInfo } from 'State/controlInfo/actions';
import { DCImportFieldEnum as Fields } from 'Common/FieldEnum';
import {
  DelimOptions,
  ImportTypeEnum,
  analyzeButtonDelay,
  DefaultConfigOptionEnum,
} from './helpers/Constants';
import { FilePickerButton, DatePickerField } from 'Common/SemanticWrapper';
import { getEndOfFiscalYear, defaultDateFormat } from 'Common/GeneralHelper';
import { createToast, toastType, errorToast } from 'Common/ToastHelper';
import { NotificationDialog } from '../../components/dialogs/NotificationDialog';

import 'PageStyles/dcimport/index.less';

class DCImportContainer extends Component {
  //the file cannot be stored in the redux store
  state = {
    file: null,
    secs: analyzeButtonDelay,
    showClearUnmatchedModal: false,
  };
  timer = null;

  componentDidMount() {
    const {
      getControlInfo,
      getConfigList,
      change,
      formValues,
      selectedConfigId,
      getUnmatchedCount,
    } = this.props;

    getControlInfo(true);
    getConfigList();
    //for the scenario of when coming back from the create new page
    //blank is pre-selected
    this.changeToConfig(
      selectedConfigId === DefaultConfigOptionEnum.New
        ? DefaultConfigOptionEnum.Blank
        : selectedConfigId
    );

    getUnmatchedCount();

    // This is because the date picker control is weird.
    // can you give more details for this comment???
    // If the form value is set before rendering, the control will appear blank
    // so we re-set it to itself to show the value.
    change(
      DC_IMPORT_SETTINGS_FORM,
      Fields.ApplicationDate,
      !formValues || !formValues[Fields.ApplicationDate]
        ? defaultDateFormat(moment())
        : formValues[Fields.ApplicationDate]
    );
  }

  componentWillUnmount() {
    clearInterval(this.timer);
  }

  componentDidUpdate(prevProps) {
    const { analysisResult, importStatus } = this.props;

    if (!prevProps.analysisResult && analysisResult) {
      this.startTimer();
    } else if (errorToast(importStatus, prevProps.importStatus, 'error')) {
      this.props.clearImporStatusError();
    } else if (!prevProps.importStatus.queued && importStatus.queued) {
      createToast(toastType.success, 'Success', 'Import queued', true);
      this.props.clearAnalyzeResults();
    }
  }

  getMainProps = () => {
    const { isReadOnly, importStatus } = this.props;

    return {
      toolbox: {
        isReadOnly,
        formName: DC_IMPORT_SETTINGS_FORM,
        saveButtonProps: {
          content: 'Import File',
          onClick: this.onImportClick,
          disabled: !this.isOkToSave() || importStatus.queuing,
          loading: importStatus.queuing,
        },
        shown: true,
      },
    };
  };

  onImportClick = () => {
    const { analysisResult } = this.props;
    this.props.queueImport(analysisResult.ImportRecordId);
  };

  isOkToSave = () => {
    return this.props.fileData.length > 0 && this.props.fileConfirmedOk;
  };

  startTimer = () => {
    this.setState({ secs: analyzeButtonDelay });
    this.timer = setInterval(() => {
      if (this.state.secs > 0) {
        this.setState({ secs: this.state.secs - 1 });
      } else {
        clearInterval(this.timer);
      }
    }, 1000);
  };

  onAnalyze = () => {
    if (this.props.unmatchedCount && this.props.unmatchedCount > 0) {
      this.setState({ showClearUnmatchedModal: true });
    } else {
      this.analyze();
    }
  };

  changeSelectedConfig = (e, { value }) => {
    this.changeToConfig(value);
    this.props.setAnalysisResult(null);
    this.props.setFileConfirmedOk(false);
    if (value === DefaultConfigOptionEnum.New) {
      this.props.history.push('/dcimport/fileconfig');
    }
  };

  changeToConfig(id) {
    const { configList, saveSelectedConfig } = this.props;
    if (id === DefaultConfigOptionEnum.Blank) {
      saveSelectedConfig(null, '', DefaultConfigOptionEnum.Blank);
      return;
    }

    const selectedIndex = configList.findIndex((config) => config.Id === id);
    const config = configList[selectedIndex];
    if (selectedIndex !== -1) {
      const headers = config.SelectedFields.split('|').map((header) =>
        header === 'Skip' ? null : header
      );
      const data = {
        ...JSON.parse(config.CustomSettings),
        [Fields.HeaderMapping]: headers.length ? headers : [],
        [Fields.ContainsHeaderInformation]: Boolean(config.IncludeHeaders),
        [Fields.ImportType]: config.ImportTypeId,
        [Fields.Delimiter]: config.Delimiter,
      };
      saveSelectedConfig(data, config.Name, id);
    } else {
      saveSelectedConfig(null, '', DefaultConfigOptionEnum.New);
    }
  }

  getSavedConfigs = () => {
    const { configList } = this.props;

    let configOptions = configList.map((config, index) => ({
      key: index,
      value: config.Id,
      text: config.Name,
    }));

    configOptions = [
      {
        key: DefaultConfigOptionEnum.Blank,
        value: DefaultConfigOptionEnum.Blank,
        text: '',
      },
      {
        key: DefaultConfigOptionEnum.New,
        value: DefaultConfigOptionEnum.New,
        text: 'CREATE NEW',
      },
    ].concat(configOptions);
    return configOptions;
  };

  onFileChange = (files) => {
    const { fileConfig } = this.props;
    const file = files[0];
    this.setState({ file });
    this.props.postImportFile(file, fileConfig[Fields.Delimiter]);
    this.props.setAnalysisResult(null);
    this.props.setFileConfirmedOk(false);
  };

  getMinDate = () => {
    if (this.props.fiscalYear) {
      return getEndOfFiscalYear(this.props.fiscalYear - 1).add(1, 'days');
    }
  };

  getAnalysisIsOkButton = () => {
    const { secs } = this.state;
    const { fileConfirmedOk } = this.props;
    const confirmText = fileConfirmedOk
      ? 'Confirmed'
      : 'I confirm the file is setup correctly';

    return (
      <Button
        primary
        content={
          secs > 0
            ? `Please confirm the numbers are correct 
        (${secs} sec)
        `
            : confirmText
        }
        disabled={secs !== 0 || fileConfirmedOk}
        onClick={() => this.props.setFileConfirmedOk(true)}
      />
    );
  };

  getAnalysisRows = () => {
    const { analysisResult } = this.props;

    if (analysisResult) {
      return (
        <Fragment>
          <Grid.Row
            columns={2}
            className={`${
              analysisResult.PercentageOfEnrolled > 75 ? 'text-red' : ''
            }`}
          >
            <Grid.Column>Percentage of Enrolled Students:</Grid.Column>
            <Grid.Column>{`${analysisResult.PercentageOfEnrolled}%`}</Grid.Column>
          </Grid.Row>
          <Grid.Row columns={2}>
            <Grid.Column>Potential Families:</Grid.Column>
            <Grid.Column>{analysisResult.PotentialFamilies}</Grid.Column>
          </Grid.Row>
          <Grid.Row columns={2}>
            <Grid.Column>Matched Students:</Grid.Column>
            <Grid.Column>{analysisResult.MatchedStudents}</Grid.Column>
          </Grid.Row>
          <Grid.Row columns={2}>
            <Grid.Column>Unmatched Students:</Grid.Column>
            <Grid.Column>{analysisResult.UnmatchedStudents}</Grid.Column>
          </Grid.Row>
          <Grid.Row columns={2}>
            <Grid.Column>Declined Students:</Grid.Column>
            <Grid.Column>{analysisResult.DeclinedStudents}</Grid.Column>
          </Grid.Row>
        </Fragment>
      );
    } else {
      return (
        <Grid.Row>
          <Grid.Column>
            Generate report to view the proccessed file prior to import.
          </Grid.Column>
        </Grid.Row>
      );
    }
  };

  getIcon = (value) => {
    return value ? (
      <Icon size={'small'} name={'check'} color={'green'} />
    ) : (
      <Icon size={'small'} name={'close'} color={'red'} />
    );
  };

  areCardsVisible = () => {
    return this.props.selectedConfigId < 0;
  };

  handleClearUnmatchedModal = (result) => {
    this.setState({ showClearUnmatchedModal: false });

    if (result) {
      this.analyze();
    }
  };

  analyze = () => {
    this.props.analyzeFile(
      this.state.file,
      this.props.formValues[Fields.ApplicationDate],
      this.props.selectedConfigId
    );
  };

  render() {
    const { fileConfig, fileName, fileData, fetching } = this.props;

    return (
      <Fragment>
        <Filter title={'Direct Certification / Exempt'} />
        <Main {...this.getMainProps()}>
          <Grid>
            <Grid.Row className={'dc-import--bottom'}>
              <Grid.Column width={8}>
                <Card fluid raised>
                  <Card.Content>
                    <Card.Header className={'dc-import--bottom'}>
                      {this.getIcon(
                        this.props.selectedConfigId !==
                          DefaultConfigOptionEnum.Blank
                      )}
                      Select Configuration
                    </Card.Header>
                    <Card.Description>
                      <Grid>
                        <Grid.Row
                          className={'dc-import--top dc-import--bottom'}
                        >
                          <Grid.Column
                            width={6}
                            className={'dc-import--name-label'}
                          >
                            Name:
                          </Grid.Column>
                          <Grid.Column width={10}>
                            <Dropdown
                              className="icon"
                              options={this.getSavedConfigs()}
                              value={this.props.selectedConfigId}
                              selection
                              fluid
                              onChange={this.changeSelectedConfig}
                              disabled={fetching}
                            />
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </Card.Description>
                  </Card.Content>
                </Card>
              </Grid.Column>
            </Grid.Row>
          </Grid>
          {!this.areCardsVisible() && (
            <Fragment>
              <Grid>
                <Grid.Row className={'dc-import--bottom'}>
                  <Grid.Column width={8}>
                    <Card fluid raised>
                      <Card.Content>
                        <Card.Header className={'dc-import--bottom'}>
                          {this.getIcon(fileData.length > 0)}
                          Configuration Settings
                        </Card.Header>
                        <Card.Description>
                          <Grid className={'dc-import--top'}>
                            <Grid.Row columns={2}>
                              <Grid.Column>Application Date:</Grid.Column>
                              <Grid.Column>
                                <Field
                                  name={Fields.ApplicationDate}
                                  component={DatePickerField}
                                  type="text"
                                  openToDate={moment()}
                                  minDate={this.getMinDate()}
                                  maxDate={moment()}
                                />
                              </Grid.Column>
                            </Grid.Row>
                            <Grid.Row columns={2}>
                              <Grid.Column>Import Type:</Grid.Column>
                              <Grid.Column>
                                {fileConfig[Fields.ImportType]
                                  ? Object.keys(ImportTypeEnum)
                                      .find(
                                        (key) =>
                                          ImportTypeEnum[key] ===
                                          fileConfig[Fields.ImportType]
                                      )
                                      .replace(new RegExp('_', 'g'), ' ')
                                  : 'No Import Type'}
                              </Grid.Column>
                            </Grid.Row>
                            <Grid.Row columns={2}>
                              <Grid.Column>Use Case Type Mapping:</Grid.Column>
                              <Grid.Column>
                                {fileConfig[Fields.UseCaseTypeMapping]
                                  ? 'Yes'
                                  : 'No'}
                              </Grid.Column>
                            </Grid.Row>
                            <Grid.Row columns={2}>
                              <Grid.Column>First Line Header:</Grid.Column>
                              <Grid.Column>
                                {fileConfig[Fields.ContainsHeaderInformation]
                                  ? 'Yes'
                                  : 'No'}
                              </Grid.Column>
                            </Grid.Row>
                            <Grid.Row columns={2}>
                              <Grid.Column>Use Address From File:</Grid.Column>
                              <Grid.Column>
                                {fileConfig[Fields.UseAddressInFile]
                                  ? 'Yes'
                                  : 'No'}
                              </Grid.Column>
                            </Grid.Row>
                            <Grid.Row columns={2}>
                              <Grid.Column>
                                Ignore Leading Zeros for State Number:
                              </Grid.Column>
                              <Grid.Column>
                                {fileConfig[
                                  Fields.IgnoreStateNumberLeadingZeros
                                ]
                                  ? 'Yes'
                                  : 'No'}
                              </Grid.Column>
                            </Grid.Row>
                            <Grid.Row columns={2}>
                              <Grid.Column>Selected File:</Grid.Column>
                              <Grid.Column>
                                <span
                                  className={classnames({
                                    'text-red': !fileName,
                                  })}
                                >
                                  {fileName ? fileName : 'No File Selected'}
                                </span>
                              </Grid.Column>
                            </Grid.Row>
                            <Grid.Row columns={2}>
                              <Grid.Column>File Length:</Grid.Column>
                              <Grid.Column>
                                <span
                                  className={classnames({
                                    'text-red': !fileData.length > 0,
                                  })}
                                >
                                  {fileData.length > 0
                                    ? `${fileData.length} lines`
                                    : 'No File Selected'}
                                </span>
                              </Grid.Column>
                            </Grid.Row>
                            <Grid.Row columns={2}>
                              <Grid.Column>File Format:</Grid.Column>
                              <Grid.Column>
                                {
                                  DelimOptions.find(
                                    (element) =>
                                      element.value ===
                                      fileConfig[Fields.Delimiter]
                                  ).label
                                }
                              </Grid.Column>
                            </Grid.Row>
                          </Grid>
                        </Card.Description>
                      </Card.Content>
                      <Card.Content extra align={'right'}>
                        <Button
                          content={'Change Settings'}
                          onClick={() => {
                            this.props.history.push('/dcimport/fileconfig');
                            this.props.setAnalysisResult(null);
                          }}
                        />
                        <FilePickerButton
                          primary
                          onFileSelect={this.onFileChange}
                        />
                      </Card.Content>
                    </Card>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
              <Grid>
                <Grid.Row className={'dc-import--bottom'}>
                  <Grid.Column width={8}>
                    <Card fluid raised>
                      <Card.Content>
                        <Card.Header className={'dc-import--bottom'}>
                          {this.getIcon(this.props.fileConfirmedOk)}
                          Confirm Settings
                        </Card.Header>
                        <Card.Description>
                          <Grid className={'dc-import--top'}>
                            {this.getAnalysisRows()}
                          </Grid>
                        </Card.Description>
                      </Card.Content>
                      <Card.Content extra align={'right'}>
                        {this.props.analysisResult ? (
                          this.getAnalysisIsOkButton()
                        ) : (
                          <Button
                            primary
                            content={'Analyze File'}
                            onClick={this.onAnalyze}
                            disabled={
                              !this.props.fileData.length > 0 ||
                              this.props.DCFetching
                            }
                            loading={this.props.DCFetching}
                          />
                        )}
                      </Card.Content>
                    </Card>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Fragment>
          )}
          <NotificationDialog
            showDialog={this.state.showClearUnmatchedModal}
            title="Clear Unmatched Student Records"
            message={`You are about to clear your Unmatched Student Records`}
            showCancel={true}
            onOkClick={() => {
              this.handleClearUnmatchedModal(true);
            }}
            onCancelClick={() => {
              this.handleClearUnmatchedModal(false);
            }}
          />
        </Main>
      </Fragment>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    configList: state.DCImportState.hub.configList,
    importStatus: state.DCImportState.hub.importStatus,
    selectedConfigId: state.DCImportState.common.selectedConfigId,
    fileConfig: state.DCImportState.common.fileConfig,
    fileName: state.DCImportState.common.fileName,
    fileData: state.DCImportState.common.fileData,
    fiscalYear: state.CommonState.controlInfo.data.FiscalYear,
    formValues: getFormValues(DC_IMPORT_SETTINGS_FORM)(state),
    analysisResult: state.DCImportState.hub.analysisResult,
    fileConfirmedOk: state.DCImportState.hub.fileConfirmedOk,
    fetching: state.CommonState.controlInfo.fetching,
    DCFetching: state.DCImportState.common.fetching,
    unmatchedCount: state.DCImportState.hub.unmatchedCount,
  };
};

export const DCImport = reduxForm({ form: DC_IMPORT_SETTINGS_FORM })(
  withRouter(
    connect(mapStateToProps, {
      getControlInfo,
      getConfigList,
      saveSelectedConfig,
      change,
      postImportFile,
      analyzeFile,
      setAnalysisResult,
      setFileConfirmedOk,
      queueImport,
      clearAnalyzeResults,
      clearImporStatusError,
      getUnmatchedCount,
    })(DCImportContainer)
  )
);



// WEBPACK FOOTER //
// ./src/pages/dcimport/DCImport.js